<template>
  <div class="emission-summary-card">
    <h3 class="summary-title">Estás contratando</h3>

    <!-- Steps del proceso de emisión (horizontal en desktop, vertical en mobile) -->
    <div class="emission-steps">
      <div class="progress-indicator-compact">
        <div class="progress-step-compact">
          <div :class="['progress-circle', currentStep >= 1 ? 'progress-circle-completed' : 'progress-circle-active']">
            <el-icon v-if="currentStep > 1"><Check /></el-icon>
            <span v-else>1</span>
          </div>
          <span :class="['progress-label', currentStep >= 1 ? 'progress-label-active' : '']">Datos Personales</span>
        </div>
        <div :class="['progress-line', 'progress-line-h', currentStep >= 2 ? 'progress-line-active' : '']" aria-hidden="true"></div>
        <div class="progress-step-compact">
          <div :class="['progress-circle', currentStep >= 2 ? 'progress-circle-completed' : currentStep === 2 ? 'progress-circle-active' : '']">
            <el-icon v-if="currentStep > 2"><Check /></el-icon>
            <span v-else>2</span>
          </div>
          <span :class="['progress-label', currentStep >= 2 ? 'progress-label-active' : '']">Dirección</span>
        </div>
        <div :class="['progress-line', 'progress-line-h', currentStep >= 3 ? 'progress-line-active' : '']" aria-hidden="true"></div>
        <div class="progress-step-compact">
          <div :class="['progress-circle', currentStep >= 3 ? 'progress-circle-completed' : currentStep === 3 ? 'progress-circle-active' : '']">
            <el-icon v-if="currentStep > 3"><Check /></el-icon>
            <span v-else>3</span>
          </div>
          <span :class="['progress-label', currentStep >= 3 ? 'progress-label-active' : '']">Datos del Vehículo</span>
        </div>
        <div
          v-if="allowEmission"
          :class="['progress-line', 'progress-line-h', currentStep >= 4 ? 'progress-line-active' : '']"
          aria-hidden="true"
        ></div>
        <div v-if="allowEmission" class="progress-step-compact">
          <div :class="['progress-circle', currentStep >= 4 ? 'progress-circle-completed' : currentStep === 4 ? 'progress-circle-active' : '']">
            <el-icon v-if="currentStep > 4"><Check /></el-icon>
            <span v-else>4</span>
          </div>
          <span :class="['progress-label', currentStep >= 4 ? 'progress-label-active' : '']">Cumplimiento 492</span>
        </div>
      </div>
    </div>

    <!-- Información del vehículo -->
    <div class="vehicle-info">
      <div class="vehicle-details">
        <div class="vehicle-year-row">
          <span class="vehicle-year">{{ quoteData.year || '-' }}</span>
        </div>
        <div class="vehicle-description" v-if="quoteData.model?.name">
          <span class="vehicle-description-text">{{ quoteData.model.name }}</span>
        </div>
      </div>
    </div>

    <!-- Tipo de plan -->
    <div class="plan-badge">
      <span class="plan-label">Tarifa</span>
      <span class="plan-type">{{ selectedPlanType.toUpperCase() }}</span>
    </div>

    <!-- Importe Total -->
    <div class="total-amount-section">
      <span class="amount-label">Importe Total</span>
      <span class="amount-value">
        ${{ formatPrice(quotesResponse?.Cobertura?.PrimaTotal) }} MXN / Anual
      </span>
    </div>

    <!-- Logo de la aseguradora -->
    <div class="insurer-logo">
      <img
        :src="insurerLogo"
        :alt="insurerAlt"
        class="logo-image"
        @error="handleImageError"
      />
    </div>

    <!-- Lista de coberturas -->
    <div class="coverages-section">
      <h4 class="coverages-title">Coberturas incluidas</h4>
      <ul class="coverages-list">
        <li
          v-for="cobertura in mainCoberturas"
          :key="cobertura.Id_Cobertura"
          class="coverage-item"
        >
          <el-icon class="coverage-icon"><Check /></el-icon>
          <span class="coverage-name">{{ cobertura.Nombre }}</span>
        </li>
      </ul>
    </div>

    <!-- Información de contacto (si está disponible) -->
    <div v-if="quoteData.personalData" class="contact-info">
      <div class="contact-item">
        <span class="contact-label">Nombre:</span>
        <span class="contact-value">{{ quoteData.personalData.fullName || '-' }}</span>
      </div>
      <div class="contact-item">
        <span class="contact-label">Teléfono:</span>
        <span class="contact-value">{{ quoteData.personalData.whatsapp || '-' }}</span>
      </div>
      <div class="contact-item">
        <span class="contact-label">Código Postal:</span>
        <span class="contact-value">{{ quoteData.personalData.postalCode || '-' }}</span>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Check } from '@element-plus/icons-vue';
import { useQuoteStore } from '@/stores/quoteStore';
import type { CotizaTodasResponse } from '@/types/quote.types';

interface Props {
  quoteData: ReturnType<typeof useQuoteStore>['quoteData'];
  quotesResponse: CotizaTodasResponse | null;
  selectedPlanType?: string;
  selectedCompany?: 'QUALITAS' | 'ANA';
  currentStep?: number;
  allowEmission?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedPlanType: 'Amplia',
  selectedCompany: 'QUALITAS',
  currentStep: 1,
  allowEmission: true,
});

const emit = defineEmits<{
  back: [];
}>();

void emit; // Reservado para futuro botón «Atrás» (el padre escucha @back).

// Función para obtener la ruta del logo según la compañía
const getLogoPath = (logoName: string): string => {
  if (typeof window === 'undefined') {
    return `/${logoName}`;
  }

  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    const script = scripts[i];
    if (script.src && script.src.includes('cotizador-widget')) {
      const scriptPath = script.src.substring(0, script.src.lastIndexOf('/'));
      return `${scriptPath}/${logoName}`;
    }
  }

  const basePath = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'));
  return `${basePath}/${logoName}`;
};

const insurerLogo = ref('');

// Actualizar logo cuando cambie la compañía
watch(() => props.selectedCompany, (company) => {
  const logoName = company === 'ANA' ? 'ana-logo.png' : 'qualitas-logo.png';
  insurerLogo.value = getLogoPath(logoName);
}, { immediate: true });

const insurerAlt = computed(() => {
  return props.selectedCompany === 'ANA' ? 'ANA Seguros' : 'Quálitas';
});

const formatPrice = (value: number | undefined | null): string => {
  if (!value && value !== 0) return '0.00';
  return value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const handleImageError = () => {
  // Fallback al logo por defecto según la compañía
  const logoName = props.selectedCompany === 'ANA' ? 'ana-logo.png' : 'qualitas-logo.png';
  insurerLogo.value = `/${logoName}`;
};

// Mostrar TODAS las coberturas amparadas del plan, ordenadas por Id_Cobertura
const mainCoberturas = computed(() => {
  if (!props.quotesResponse?.Cobertura?.Coberturas) return [];
  
  const coberturas = props.quotesResponse.Cobertura.Coberturas.filter(
    (c) => c.EstaAmparada
  );
  
  // Ordenar por Id_Cobertura para mantener consistencia
  coberturas.sort((a, b) => a.Id_Cobertura - b.Id_Cobertura);
  
  return coberturas;
});
</script>
<style src="@/styles/emission-summary.css"></style>

